import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Lightbulb, Code, Rocket } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Understand the Business",
    description:
      "We start by learning what is slowing your growth online and what needs to improve.",
  },
  {
    number: "02",
    icon: Lightbulb,
    title: "Define the Right Solution",
    description:
      "We map out the service, structure, and priorities before anything gets built.",
  },
  {
    number: "03",
    icon: Code,
    title: "Build With Purpose",
    description:
      "Everything is created for usability, visibility, and long-term performance.",
  },
  {
    number: "04",
    icon: Rocket,
    title: "Launch and Support",
    description:
      "We stay involved with updates, improvements, and ongoing support after launch.",
  },
];

const leftVariants = {
  hidden: { opacity: 0, x: -24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: 28, y: 14 },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

export const ProcessSection = () => {
  return (
    <section className="relative isolate overflow-hidden bg-[linear-gradient(135deg,#fbfaff_0%,#ffffff_48%,#f5fbff_100%)] py-16 sm:py-20 lg:py-28">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-28 top-12 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-28 right-0 h-96 w-96 rounded-full bg-sky-300/20 blur-3xl" />
        <div className="absolute left-[42%] top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-white/70 blur-3xl" />

        <div className="absolute right-10 top-12 hidden h-44 w-44 opacity-25 lg:block">
          <div className="h-full w-full bg-[radial-gradient(circle,rgba(124,58,237,0.38)_1px,transparent_1px)] [background-size:13px_13px]" />
        </div>

        <div className="absolute bottom-12 left-10 hidden h-52 w-52 opacity-25 lg:block">
          <div className="h-full w-full bg-[radial-gradient(circle,rgba(14,165,233,0.38)_1px,transparent_1px)] [background-size:14px_14px]" />
        </div>
      </div>

      <div className="container-custom relative z-10">
        <div className="grid items-center gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16 xl:gap-20">
          <motion.div
            variants={leftVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.35 }}
            className="max-w-xl"
          >
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              How We Work
            </span>

            <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
              A Clear Process From Strategy to{" "}
              <span className="gradient-text">Launch</span>
            </h2>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              We follow a simple, structured process so every project moves
              forward with clarity, purpose, and long-term performance in mind.
            </p>

            {/* <div className="mt-8 h-1 w-16 rounded-full bg-gradient-to-r from-primary to-sky-400" /> */}
          </motion.div>

          <motion.div
            variants={listVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="relative space-y-4 sm:space-y-5"
          >
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.div
                  key={step.number}
                  variants={cardVariants}
                  whileHover={{
                    y: -4,
                    transition: { duration: 0.22, ease: "easeOut" },
                  }}
                  className={`group relative ${
                    index === 0
                      ? "lg:ml-4"
                      : index === 1
                      ? "lg:ml-0"
                      : index === 2
                      ? "lg:ml-4"
                      : "lg:ml-0"
                  }`}
                >
                  <div className="absolute -inset-3 rounded-[2rem] bg-primary/10 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />

                  <article className="relative grid min-h-[112px] grid-cols-[112px_1fr] overflow-visible sm:grid-cols-[150px_1fr] lg:grid-cols-[175px_1fr]">
                    <div className="relative z-20 flex items-center justify-center">
                      <div className="absolute inset-y-0 left-0 right-[-26px] bg-white/92 shadow-[0_18px_45px_rgba(15,23,42,0.08)] ring-1 ring-primary/5 backdrop-blur-xl [clip-path:polygon(0_0,84%_0,100%_50%,84%_100%,0_100%)]" />

                      <span className="absolute left-5 top-1/2 -translate-y-1/2 select-none text-6xl font-black leading-none text-primary/[0.06] sm:left-7 sm:text-7xl">
                        {step.number}
                      </span>

                      <span className="relative z-10 text-3xl font-black leading-none tracking-tight text-primary sm:text-4xl lg:text-5xl">
                        {step.number}
                      </span>
                    </div>

                    <div className="relative z-10 flex items-center bg-white/88 px-8 py-5 pl-12 shadow-[0_18px_45px_rgba(15,23,42,0.07)] ring-1 ring-primary/5 backdrop-blur-xl [clip-path:polygon(0_0,96%_0,100%_50%,96%_100%,0_100%)] sm:px-10 sm:py-6 sm:pl-14 lg:pl-16">
                      <div className="absolute left-[-24px] top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-[linear-gradient(135deg,#7c3aed,#4f46e5)] text-white shadow-[0_14px_30px_rgba(124,58,237,0.28)] [clip-path:polygon(25%_6%,75%_6%,100%_50%,75%_94%,25%_94%,0_50%)] transition-transform duration-300 group-hover:scale-105 sm:h-14 sm:w-14">
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                      </div>

                      <div className="min-w-0">
                        <h3 className="font-display text-base font-bold leading-snug text-foreground transition-colors duration-300 group-hover:text-primary sm:text-lg lg:text-xl">
                          {step.title}
                        </h3>

                        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </article>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};